/**
 * Bulk-import specs for the four travel sheets in the organizer workbook
 * (Flights, Lodging, Transport, Other Travel). Each maps to one
 * `travelItems` doc with `details.kind = 'flight' | 'lodging' | 'transport'
 * | 'other'`. All four specs share an audience cell + reminder block; the
 * per-kind columns differ.
 *
 * Audience (ADR-120 Option B): one cell per row — either a named-audience
 * name or one attendee email. Server detects `@` to dispatch. Multi-
 * audience targeting goes through the admin compose form, not the
 * workbook.
 *
 * The unified `ExcelTravelImportDialog` consumes these via a `kind` prop,
 * converts event-local datetimes to UTC using the event timezone, and
 * dispatches to the single `bulkCreateTravelItems` callable. Server-side
 * resolution covers location labels, audience cells, and the optional
 * session link by title.
 */
import type {
  FlightDirection,
  TravelItemKind,
  TransportMode,
} from "../types/travel-item";
import { airportTimezone } from "../utils/airportTimezones";
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { EMAIL_PATTERN, normalize } from "./importTypes";

const LOCAL_DATETIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/;

function parseLocalDateTime(raw: string | undefined): string | null {
  const value = normalize(raw);
  if (!value) return null;
  const m = LOCAL_DATETIME_PATTERN.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function parseReminderEnabled(
  raw: string | undefined,
): { ok: true; value: boolean } | { ok: false; message: string } {
  const value = normalize(raw).toLowerCase();
  if (!value) return { ok: true, value: false };
  if (value === "true" || value === "yes" || value === "1") {
    return { ok: true, value: true };
  }
  if (value === "false" || value === "no" || value === "0") {
    return { ok: true, value: false };
  }
  return {
    ok: false,
    message: 'reminderEnabled must be TRUE or FALSE when provided.',
  };
}

function parseInteger(raw: string | undefined): number | null {
  const value = normalize(raw);
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : null;
}

interface BaseAudienceFields {
  /** Single audience cell — name or email. Required for travel rows. */
  audience: string;
  reminder: {
    enabled: boolean;
    leadMinutes: number;
    body: string;
  } | null;
}

interface ParseAudienceResult {
  ok: true;
  data: BaseAudienceFields;
}
interface ParseAudienceFail {
  ok: false;
  message: string;
}

/**
 * Validates the shared columns: `audience` (single cell, name or
 * email) and the reminder triplet. Travel rows require a non-empty
 * audience — there's no category-default fallback like sessions.
 */
function parseAudienceAndReminder(raw: {
  audience?: string;
  reminderEnabled?: string;
  reminderLeadMinutes?: string;
  reminderBody?: string;
}): ParseAudienceResult | ParseAudienceFail {
  const audienceRaw = normalize(raw.audience);
  if (!audienceRaw) {
    return {
      ok: false,
      message:
        "audience is required — a group name (e.g. 'All Members') or one attendee email.",
    };
  }
  if (audienceRaw.includes("@") && !EMAIL_PATTERN.test(audienceRaw)) {
    return { ok: false, message: `Invalid audience email: "${audienceRaw}"` };
  }
  const audience = audienceRaw.includes("@")
    ? audienceRaw.toLowerCase()
    : audienceRaw;

  const enabledResult = parseReminderEnabled(raw.reminderEnabled);
  if (!enabledResult.ok) return { ok: false, message: enabledResult.message };

  const enabled = enabledResult.value;
  let reminder: BaseAudienceFields["reminder"] = null;
  if (enabled) {
    const lead = parseInteger(raw.reminderLeadMinutes);
    if (lead === null || lead <= 0) {
      return {
        ok: false,
        message:
          "reminderLeadMinutes must be a positive integer when reminderEnabled is TRUE.",
      };
    }
    const body = normalize(raw.reminderBody);
    if (!body) {
      return {
        ok: false,
        message: "reminderBody is required when reminderEnabled is TRUE.",
      };
    }
    reminder = { enabled: true, leadMinutes: lead, body };
  }

  return { ok: true, data: { audience, reminder } };
}

const AUDIENCE_COL_NOTE =
  "either a group name (e.g. 'All Members', 'Skiers') or one attendee email";

// ── Flights ──

export interface RawFlightImportRow extends Record<string, string | undefined> {
  audience?: string;
  direction?: string;
  airline?: string;
  flightNumber?: string;
  departureAirport?: string;
  departureTerminal?: string;
  departureAt?: string;
  arrivalAirport?: string;
  arrivalTerminal?: string;
  arrivalAt?: string;
  confirmationCode?: string;
  notes?: string;
  reminderEnabled?: string;
  reminderLeadMinutes?: string;
  reminderBody?: string;
}

export interface ParsedFlightImportRow extends BaseAudienceFields {
  kind: "flight";
  direction: FlightDirection | null;
  airline: string | null;
  flightNumber: string | null;
  departureAirport: string | null;
  departureTerminal: string | null;
  departureAtLocal: string | null;
  arrivalAirport: string | null;
  arrivalTerminal: string | null;
  arrivalAtLocal: string | null;
  confirmationCode: string | null;
  notes: string | null;
}

function parseFlightDirection(
  raw: string | undefined,
): { ok: true; value: FlightDirection | null } | { ok: false; message: string } {
  const value = normalize(raw).toLowerCase();
  if (!value) return { ok: true, value: null };
  if (value === "inbound" || value === "in" || value === "arrival") {
    return { ok: true, value: "inbound" };
  }
  if (value === "outbound" || value === "out" || value === "departure") {
    return { ok: true, value: "outbound" };
  }
  return {
    ok: false,
    message: `direction must be "inbound" or "outbound" when provided (got "${raw}").`,
  };
}

function parseAirportCode(raw: string | undefined): string | null {
  const value = normalize(raw).toUpperCase();
  return value || null;
}

export const flightImportSpec: ImportSpec<
  RawFlightImportRow,
  ParsedFlightImportRow
> = {
  sheetName: "Flights",
  purpose:
    "Travel — flights. One row per flight per audience. Reference: travel-item.ts (kind: flight).",
  columns: [
    { key: "audience", note: AUDIENCE_COL_NOTE },
    {
      key: "direction",
      note: 'inbound (arriving to event) or outbound (leaving event)',
    },
    { key: "airline", note: "" },
    { key: "flightNumber", note: "e.g. UA1234" },
    { key: "departureAirport", note: "IATA code preferred (DEN, JFK, ...)" },
    { key: "departureTerminal", note: "optional" },
    { key: "departureAt", note: "YYYY-MM-DD HH:MM in airport-local time" },
    { key: "arrivalAirport", note: "" },
    { key: "arrivalTerminal", note: "" },
    { key: "arrivalAt", note: "YYYY-MM-DD HH:MM in airport-local time" },
    {
      key: "confirmationCode",
      note: "the airline confirmation / record locator",
    },
    { key: "notes", note: "free-form" },
    {
      key: "reminderEnabled",
      note: "TRUE to send a push reminder before departure",
    },
    {
      key: "reminderLeadMinutes",
      note: "minutes before departureAt (e.g. 180 = 3 hours)",
    },
    { key: "reminderBody", note: "the push notification body verbatim" },
  ],
  example: {
    audience: "alice@example.com",
    direction: "inbound",
    airline: "United Airlines",
    flightNumber: "UA1234",
    departureAirport: "JFK",
    departureTerminal: "7",
    departureAt: "2026-02-12 06:30",
    arrivalAirport: "SLC",
    arrivalTerminal: "B",
    arrivalAt: "2026-02-12 09:45",
    confirmationCode: "ABC123",
    notes: "Aisle seat requested",
    reminderEnabled: "TRUE",
    reminderLeadMinutes: "180",
    reminderBody:
      "Heads up — your flight to SLC departs in 3 hours. JFK Terminal 7.",
  },
  identity: {
    idPrefix: "travel_flight",
    label: (row) => row.flightNumber ?? `${row.departureAirport ?? "flight"} to ${row.arrivalAirport ?? "arrival"}`,
    naturalKey: (row) => [
      row.audience,
      row.direction,
      row.flightNumber,
      row.departureAtLocal,
      row.departureAirport,
      row.arrivalAirport,
    ],
  },
  validate(raw, lineNumber) {
    const aud = parseAudienceAndReminder(raw);
    const dir = parseFlightDirection(raw.direction);
    const dep = parseLocalDateTime(raw.departureAt);
    const arr = parseLocalDateTime(raw.arrivalAt);

    const base: Omit<ParsedRow<ParsedFlightImportRow>, "status" | "message"> = {
      lineNumber,
      data: {
        kind: "flight",
        audience: aud.ok ? aud.data.audience : "",
        reminder: aud.ok ? aud.data.reminder : null,
        direction: dir.ok ? dir.value : null,
        airline: normalize(raw.airline) || null,
        flightNumber: normalize(raw.flightNumber) || null,
        departureAirport: parseAirportCode(raw.departureAirport),
        departureTerminal: normalize(raw.departureTerminal) || null,
        departureAtLocal: dep,
        arrivalAirport: parseAirportCode(raw.arrivalAirport),
        arrivalTerminal: normalize(raw.arrivalTerminal) || null,
        arrivalAtLocal: arr,
        confirmationCode: normalize(raw.confirmationCode) || null,
        notes: normalize(raw.notes) || null,
      },
    };

    if (!aud.ok) return invalid(base, aud.message);
    if (!dir.ok) return invalid(base, dir.message);
    const departureAirport = base.data.departureAirport;
    const arrivalAirport = base.data.arrivalAirport;
    if (dep && !departureAirport) {
      return invalid(
        base,
        "departureAirport is required when departureAt is provided.",
      );
    }
    if (arr && !arrivalAirport) {
      return invalid(base, "arrivalAirport is required when arrivalAt is provided.");
    }
    if (departureAirport && !airportTimezone(departureAirport)) {
      return invalid(
        base,
        `Unsupported departureAirport timezone for ${departureAirport}. Add it to airportTimezones before importing.`,
      );
    }
    if (arrivalAirport && !airportTimezone(arrivalAirport)) {
      return invalid(
        base,
        `Unsupported arrivalAirport timezone for ${arrivalAirport}. Add it to airportTimezones before importing.`,
      );
    }
    if (raw.departureAt && !dep) {
      return invalid(
        base,
        'departureAt must be in "YYYY-MM-DD HH:MM" format.',
      );
    }
    if (raw.arrivalAt && !arr) {
      return invalid(base, 'arrivalAt must be in "YYYY-MM-DD HH:MM" format.');
    }
    if (aud.ok && aud.data.reminder && !dep) {
      return invalid(
        base,
        "departureAt is required when reminderEnabled is TRUE.",
      );
    }

    return pending(base);
  },
};

// ── Lodging ──

export interface RawLodgingImportRow extends Record<string, string | undefined> {
  audience?: string;
  locationLabel?: string;
  checkInAt?: string;
  checkOutAt?: string;
  confirmationCode?: string;
  notes?: string;
  reminderEnabled?: string;
  reminderLeadMinutes?: string;
  reminderBody?: string;
}

export interface ParsedLodgingImportRow extends BaseAudienceFields {
  kind: "lodging";
  locationLabel: string | null;
  checkInAtLocal: string | null;
  checkOutAtLocal: string | null;
  confirmationCode: string | null;
  notes: string | null;
}

export const lodgingImportSpec: ImportSpec<
  RawLodgingImportRow,
  ParsedLodgingImportRow
> = {
  sheetName: "Lodging",
  purpose:
    "Travel — hotels and lodging. Reference: travel-item.ts (kind: lodging).",
  columns: [
    { key: "audience", note: AUDIENCE_COL_NOTE },
    {
      key: "locationLabel",
      note: "the hotel's Location label (matches a row in the Locations sheet)",
    },
    { key: "checkInAt", note: "YYYY-MM-DD HH:MM in hotel-local time" },
    { key: "checkOutAt", note: "YYYY-MM-DD HH:MM" },
    { key: "confirmationCode", note: "hotel reservation number" },
    { key: "notes", note: "free-form" },
    { key: "reminderEnabled", note: "TRUE for a check-in reminder" },
    { key: "reminderLeadMinutes", note: "minutes before checkInAt" },
    { key: "reminderBody", note: "the push notification body" },
  ],
  example: {
    audience: "alice@example.com",
    locationLabel: "Stein Eriksen Lodge",
    checkInAt: "2026-02-12 16:00",
    checkOutAt: "2026-02-15 11:00",
    confirmationCode: "STN-987654",
    notes: "Room block under 'Apex Winter Summit'",
    reminderEnabled: "FALSE",
    reminderLeadMinutes: "",
    reminderBody: "",
  },
  identity: {
    idPrefix: "travel_lodging",
    label: (row) => row.locationLabel ?? `lodging for ${row.audience}`,
    naturalKey: (row) => [
      row.audience,
      row.locationLabel,
      row.checkInAtLocal,
      row.checkOutAtLocal,
      row.confirmationCode,
    ],
  },
  validate(raw, lineNumber) {
    const aud = parseAudienceAndReminder(raw);
    const checkIn = parseLocalDateTime(raw.checkInAt);
    const checkOut = parseLocalDateTime(raw.checkOutAt);

    const base: Omit<ParsedRow<ParsedLodgingImportRow>, "status" | "message"> =
      {
        lineNumber,
        data: {
          kind: "lodging",
          audience: aud.ok ? aud.data.audience : "",
          reminder: aud.ok ? aud.data.reminder : null,
          locationLabel: normalize(raw.locationLabel) || null,
          checkInAtLocal: checkIn,
          checkOutAtLocal: checkOut,
          confirmationCode: normalize(raw.confirmationCode) || null,
          notes: normalize(raw.notes) || null,
        },
      };

    if (!aud.ok) return invalid(base, aud.message);
    if (raw.checkInAt && !checkIn) {
      return invalid(base, 'checkInAt must be in "YYYY-MM-DD HH:MM" format.');
    }
    if (raw.checkOutAt && !checkOut) {
      return invalid(base, 'checkOutAt must be in "YYYY-MM-DD HH:MM" format.');
    }
    if (aud.ok && aud.data.reminder && !checkIn) {
      return invalid(
        base,
        "checkInAt is required when reminderEnabled is TRUE.",
      );
    }

    return pending(base);
  },
};

// ── Transport ──

const TRANSPORT_MODES: readonly TransportMode[] = [
  "shuttle",
  "car",
  "rideshare",
  "taxi",
  "train",
  "ferry",
  "walk",
  "custom",
] as const;

export interface RawTransportImportRow
  extends Record<string, string | undefined> {
  audience?: string;
  mode?: string;
  pickupLocationLabel?: string;
  dropoffLocationLabel?: string;
  pickupAt?: string;
  code?: string;
  voucherUrl?: string;
  spendLimitAmount?: string;
  spendLimitCurrency?: string;
  validityFrom?: string;
  validityUntil?: string;
  sessionLink?: string;
  notes?: string;
  reminderEnabled?: string;
  reminderLeadMinutes?: string;
  reminderBody?: string;
}

export interface ParsedTransportImportRow extends BaseAudienceFields {
  kind: "transport";
  mode: TransportMode;
  pickupLocationLabel: string | null;
  dropoffLocationLabel: string | null;
  pickupAtLocal: string | null;
  code: string | null;
  voucherUrl: string | null;
  spendLimit: { amount: number; currency: string } | null;
  validityFromLocal: string | null;
  validityUntilLocal: string | null;
  sessionLinkTitle: string | null;
  notes: string | null;
}

function parseNumber(raw: string | undefined): number | null {
  const v = normalize(raw);
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function isValidUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

export const transportImportSpec: ImportSpec<
  RawTransportImportRow,
  ParsedTransportImportRow
> = {
  sheetName: "Transport",
  purpose:
    "Travel — shuttles, rideshares, taxis, trains, ferries, walks, custom. Reference: travel-item.ts (kind: transport).",
  columns: [
    { key: "audience", note: AUDIENCE_COL_NOTE },
    {
      key: "mode",
      note: "shuttle | car | rideshare | taxi | train | ferry | walk | custom",
    },
    {
      key: "pickupLocationLabel",
      note: "Location label for the pickup (matches a row in the Locations sheet). Optional for open-ended vouchers.",
    },
    { key: "dropoffLocationLabel", note: "Location label for the dropoff. Optional for open-ended vouchers." },
    {
      key: "pickupAt",
      note: "YYYY-MM-DD HH:MM in pickup-local time. LEAVE BLANK for open-ended vouchers (e.g. ride code valid throughout event).",
    },
    {
      key: "code",
      note: "rideshare voucher code, ride confirmation, etc. (form label: 'Code (rideshare voucher / confirmation)')",
    },
    {
      key: "voucherUrl",
      note: "optional — Uber voucher claim link copied from the Uber Business dashboard",
    },
    {
      key: "spendLimitAmount",
      note: "optional — cap on the voucher's value (e.g. 200 for $200). Leave blank if uncapped.",
    },
    {
      key: "spendLimitCurrency",
      note: "optional — 3-letter ISO currency code (e.g. USD). Required if spendLimitAmount is set.",
    },
    {
      key: "validityFrom",
      note: "optional — YYYY-MM-DD HH:MM, when an open-ended voucher becomes usable.",
    },
    {
      key: "validityUntil",
      note: "optional — YYYY-MM-DD HH:MM, when an open-ended voucher expires.",
    },
    {
      key: "sessionLink",
      note: "optional — a Session title to badge this travel item as 'for: <session>'",
    },
    { key: "notes", note: "free-form" },
    { key: "reminderEnabled", note: "TRUE for a 'leaving soon' reminder. Requires pickupAt — not valid for open-ended vouchers." },
    { key: "reminderLeadMinutes", note: "minutes before pickupAt" },
    {
      key: "reminderBody",
      note: "the push notification body — include the code if you want it inline",
    },
  ],
  example: {
    audience: "Dinner — Friday Mario's",
    mode: "rideshare",
    pickupLocationLabel: "Mario's Trattoria",
    dropoffLocationLabel: "Stein Eriksen Lodge",
    pickupAt: "2026-02-13 22:00",
    code: "EVENTX20",
    voucherUrl: "https://r.uber.com/example-voucher",
    spendLimitAmount: "40",
    spendLimitCurrency: "USD",
    validityFrom: "",
    validityUntil: "",
    sessionLink: "Dinner at Mario's",
    notes:
      "Use code EVENTX20 in the Lyft / Uber app — covers up to $40 per ride.",
    reminderEnabled: "TRUE",
    reminderLeadMinutes: "30",
    reminderBody:
      "Your ride home is on us — code EVENTX20 in Lyft or Uber, good through midnight.",
  },
  identity: {
    idPrefix: "travel_transport",
    label: (row) => row.code ?? row.sessionLinkTitle ?? `${row.mode} for ${row.audience}`,
    naturalKey: (row) => [
      row.audience,
      row.mode,
      row.pickupAtLocal,
      row.code,
      row.pickupLocationLabel,
      row.dropoffLocationLabel,
      row.sessionLinkTitle,
    ],
  },
  validate(raw, lineNumber) {
    const aud = parseAudienceAndReminder(raw);
    const modeRaw = normalize(raw.mode).toLowerCase();
    const pickup = parseLocalDateTime(raw.pickupAt);
    const validityFrom = parseLocalDateTime(raw.validityFrom);
    const validityUntil = parseLocalDateTime(raw.validityUntil);
    const voucherUrl = normalize(raw.voucherUrl) || null;
    const spendAmount = parseNumber(raw.spendLimitAmount);
    const spendCurrency = normalize(raw.spendLimitCurrency).toUpperCase();
    const spendLimit =
      spendAmount !== null && spendCurrency
        ? { amount: spendAmount, currency: spendCurrency }
        : null;

    const base: Omit<ParsedRow<ParsedTransportImportRow>, "status" | "message"> =
      {
        lineNumber,
        data: {
          kind: "transport",
          audience: aud.ok ? aud.data.audience : "",
          reminder: aud.ok ? aud.data.reminder : null,
          mode: "shuttle",
          pickupLocationLabel: normalize(raw.pickupLocationLabel) || null,
          dropoffLocationLabel: normalize(raw.dropoffLocationLabel) || null,
          pickupAtLocal: pickup,
          code: normalize(raw.code) || null,
          voucherUrl,
          spendLimit,
          validityFromLocal: validityFrom,
          validityUntilLocal: validityUntil,
          sessionLinkTitle: normalize(raw.sessionLink) || null,
          notes: normalize(raw.notes) || null,
        },
      };

    if (!aud.ok) return invalid(base, aud.message);
    if (!modeRaw) {
      return invalid(base, "mode is required");
    }
    if (!(TRANSPORT_MODES as readonly string[]).includes(modeRaw)) {
      return invalid(
        base,
        `Unknown mode "${modeRaw}". Must be one of: ${TRANSPORT_MODES.join(", ")}`,
      );
    }
    if (raw.pickupAt && !pickup) {
      return invalid(base, 'pickupAt must be in "YYYY-MM-DD HH:MM" format.');
    }
    if (voucherUrl && !isValidUrl(voucherUrl)) {
      return invalid(base, "voucherUrl must be a valid URL.");
    }
    if (raw.validityFrom && !validityFrom) {
      return invalid(base, 'validityFrom must be in "YYYY-MM-DD HH:MM" format.');
    }
    if (raw.validityUntil && !validityUntil) {
      return invalid(
        base,
        'validityUntil must be in "YYYY-MM-DD HH:MM" format.',
      );
    }
    if (spendAmount !== null && !spendCurrency) {
      return invalid(
        base,
        "spendLimitCurrency is required when spendLimitAmount is set.",
      );
    }
    if (spendAmount === null && spendCurrency) {
      return invalid(
        base,
        "spendLimitAmount is required when spendLimitCurrency is set.",
      );
    }
    if (aud.ok && aud.data.reminder && !pickup) {
      return invalid(
        base,
        "pickupAt is required when reminderEnabled is TRUE (open-ended vouchers can't have reminders).",
      );
    }

    return pending({ ...base, data: { ...base.data, mode: modeRaw as TransportMode } });
  },
};

// ── Other Travel ──

export interface RawOtherTravelImportRow
  extends Record<string, string | undefined> {
  audience?: string;
  title?: string;
  description?: string;
  time?: string;
  notes?: string;
  reminderEnabled?: string;
  reminderLeadMinutes?: string;
  reminderBody?: string;
}

export interface ParsedOtherTravelImportRow extends BaseAudienceFields {
  kind: "other";
  title: string;
  description: string | null;
  timeLocal: string | null;
  notes: string | null;
}

export const otherTravelImportSpec: ImportSpec<
  RawOtherTravelImportRow,
  ParsedOtherTravelImportRow
> = {
  sheetName: "Other Travel",
  purpose:
    "Travel — anything that doesn't fit the other three (boat charter, helicopter, baggage transfer). Reference: travel-item.ts (kind: other).",
  columns: [
    { key: "audience", note: AUDIENCE_COL_NOTE },
    { key: "title", note: "required — short label" },
    { key: "description", note: "optional — fuller description" },
    { key: "time", note: "YYYY-MM-DD HH:MM (the relevant time, freeform)" },
    { key: "notes", note: "free-form" },
    { key: "reminderEnabled", note: "TRUE for a reminder" },
    { key: "reminderLeadMinutes", note: "minutes before time" },
    { key: "reminderBody", note: "the push notification body" },
  ],
  example: {
    audience: "All Invitees",
    title: "Baggage Pickup",
    description:
      "Bell staff will collect bags from rooms after breakfast for shuttle loading.",
    time: "2026-02-15 10:00",
    notes: "",
    reminderEnabled: "TRUE",
    reminderLeadMinutes: "60",
    reminderBody:
      "Bell staff collects bags at 10am — leave them outside your door by 9:45.",
  },
  identity: {
    idPrefix: "travel_other",
    label: (row) => row.title,
    naturalKey: (row) => [
      row.audience,
      row.title,
      row.timeLocal,
    ],
  },
  validate(raw, lineNumber) {
    const aud = parseAudienceAndReminder(raw);
    const title = normalize(raw.title);
    const time = parseLocalDateTime(raw.time);

    const base: Omit<
      ParsedRow<ParsedOtherTravelImportRow>,
      "status" | "message"
    > = {
      lineNumber,
      data: {
        kind: "other",
        audience: aud.ok ? aud.data.audience : "",
        reminder: aud.ok ? aud.data.reminder : null,
        title,
        description: normalize(raw.description) || null,
        timeLocal: time,
        notes: normalize(raw.notes) || null,
      },
    };

    if (!aud.ok) return invalid(base, aud.message);
    if (!title) return invalid(base, "title is required");
    if (raw.time && !time) {
      return invalid(base, 'time must be in "YYYY-MM-DD HH:MM" format.');
    }
    if (aud.ok && aud.data.reminder && !time) {
      return invalid(base, "time is required when reminderEnabled is TRUE.");
    }

    return pending(base);
  },
};

/** Lookup a travel spec by kind for the unified dialog. */
export function travelSpecFor(kind: TravelItemKind) {
  switch (kind) {
    case "flight":
      return flightImportSpec;
    case "lodging":
      return lodgingImportSpec;
    case "transport":
      return transportImportSpec;
    case "other":
      return otherTravelImportSpec;
  }
}
